/**
 * MOS (Mean Opinion Score) approximation from WebRTC RTCStats.
 *
 * Uses a simplified ITU-T G.107 E-Model:
 *   R = 93.2 − Id − Ie_eff
 *   Id  = 0.024·T + 0.11·(T−177.3)·(T>177.3)    (delay impairment, T = one-way delay ms)
 *   Ie_eff = Ie + (95 − Ie)·(loss / (loss + Bpl))  (loss impairment)
 *   MOS = 1 + 0.035·R + 7e-6·R·(R−60)·(100−R)     (R → 1..4.5 mapping)
 *
 * Codec equipment-impairment factors (Ie) and packet-loss robustness (Bpl):
 *   PCMA/PCMU: Ie=0,  Bpl=4.3
 *   G.722:     Ie=10, Bpl=15
 *   Opus:      Ie=11, Bpl=25  (Opus FEC is very loss-resilient)
 *
 * This is an approximation — perceived quality differs per listener — but it
 * tracks real-world degradation well enough to drive a 5-bar UI indicator and
 * raise red flags above ~5% loss / 50ms jitter / 250ms RTT.
 */

export interface QualitySample {
  /** 1.0 (poor) .. 4.5 (excellent). Null until enough stats are collected. */
  mos: number | null;
  /** 0..5 bars derived from MOS. */
  bars: 0 | 1 | 2 | 3 | 4 | 5;
  /** Inbound packet loss percent (0..100). */
  lossPct: number;
  /** Inbound jitter in ms. */
  jitterMs: number;
  /** Round-trip time in ms (from candidate-pair). */
  rttMs: number;
  /** Codec being received (e.g. "PCMA/8000/1"). */
  codecIn: string;
  /** Codec being sent. */
  codecOut: string;
  /** Total bytes sent since call start (artıyorsa karşı tarafa RTP gidiyor). */
  bytesSent: number;
  /** Total bytes received since call start. */
  bytesReceived: number;
  /** When the sample was taken. */
  ts: number;
}

const CODEC_IE: Record<string, { ie: number; bpl: number }> = {
  pcma: { ie: 0,  bpl: 4.3 },
  pcmu: { ie: 0,  bpl: 4.3 },
  g722: { ie: 10, bpl: 15 },
  opus: { ie: 11, bpl: 25 },
};

function codecKey(label: string): string {
  const m = /^([a-zA-Z]+)/.exec(label);
  return (m ? m[1] : '').toLowerCase();
}

/** Convert R-factor (0..100) → MOS (1..4.5). */
function rToMos(R: number): number {
  if (R <= 0) return 1;
  if (R >= 100) return 4.5;
  const mos = 1 + 0.035 * R + 7e-6 * R * (R - 60) * (100 - R);
  return Math.max(1, Math.min(4.5, mos));
}

function computeMos(opts: {
  lossPct: number; jitterMs: number; rttMs: number; codecLabel: string;
}): number {
  const { lossPct, jitterMs, rttMs, codecLabel } = opts;
  const c = CODEC_IE[codecKey(codecLabel)] ?? CODEC_IE.pcma;

  // One-way delay ≈ RTT/2 + half jitter buffer (assume ~jitter*2).
  const oneWay = (rttMs / 2) + jitterMs * 2;
  const Id = 0.024 * oneWay + (oneWay > 177.3 ? 0.11 * (oneWay - 177.3) : 0);
  const Ie_eff = c.ie + (95 - c.ie) * (lossPct / (lossPct + c.bpl));
  const R = 93.2 - Id - Ie_eff;
  return rToMos(R);
}

function mosToBars(mos: number | null): 0 | 1 | 2 | 3 | 4 | 5 {
  if (mos == null) return 0;
  if (mos >= 4.2) return 5;
  if (mos >= 3.8) return 4;
  if (mos >= 3.2) return 3;
  if (mos >= 2.5) return 2;
  if (mos >= 1.8) return 1;
  return 0;
}

/**
 * Stateful sampler — call sample(pc) every few seconds during an active call.
 * Tracks delta packets/lost between successive getStats calls so loss% is a
 * rolling window value rather than the lifetime average (which would skew low
 * after a brief glitch).
 */
export class QualitySampler {
  private prevPacketsLost = 0;
  private prevPacketsReceived = 0;
  private prevTs = 0;

  reset(): void {
    this.prevPacketsLost = 0;
    this.prevPacketsReceived = 0;
    this.prevTs = 0;
  }

  async sample(pc: RTCPeerConnection): Promise<QualitySample | null> {
    const stats = await pc.getStats();
    const codecMap = new Map<string, string>();
    stats.forEach((s: any) => {
      if (s.type === 'codec') {
        const ch = s.channels ?? 1;
        codecMap.set(s.id, `${s.mimeType?.replace('audio/', '') ?? '?'}/${s.clockRate ?? '?'}/${ch}`);
      }
    });

    let codecIn = 'unknown';
    let codecOut = 'unknown';
    let jitterMs = 0;
    let rttMs = 0;
    let packetsLost = 0;
    let packetsReceived = 0;
    let bytesSent = 0;
    let bytesReceived = 0;

    stats.forEach((s: any) => {
      if (s.type === 'inbound-rtp' && s.kind === 'audio') {
        codecIn = codecMap.get(s.codecId) ?? codecIn;
        jitterMs = (s.jitter ?? 0) * 1000;
        packetsLost = s.packetsLost ?? 0;
        packetsReceived = s.packetsReceived ?? 0;
        bytesReceived = s.bytesReceived ?? 0;
      }
      if (s.type === 'outbound-rtp' && s.kind === 'audio') {
        codecOut = codecMap.get(s.codecId) ?? codecOut;
        bytesSent = s.bytesSent ?? 0;
      }
      if (s.type === 'candidate-pair' && (s.state === 'succeeded' || s.nominated)) {
        if (typeof s.currentRoundTripTime === 'number') rttMs = s.currentRoundTripTime * 1000;
      }
    });

    // Windowed loss%: only count packets that arrived since the last sample.
    const deltaLost = Math.max(0, packetsLost - this.prevPacketsLost);
    const deltaRecv = Math.max(0, packetsReceived - this.prevPacketsReceived);
    const deltaTotal = deltaLost + deltaRecv;
    const lossPct = deltaTotal > 0 ? (deltaLost / deltaTotal) * 100 : 0;
    this.prevPacketsLost = packetsLost;
    this.prevPacketsReceived = packetsReceived;
    const now = Date.now();
    const firstSample = this.prevTs === 0;
    this.prevTs = now;
    if (firstSample || deltaTotal < 10) {
      // Not enough data yet for a reliable MOS — return shape but no score.
      return { mos: null, bars: 0, lossPct, jitterMs, rttMs, codecIn, codecOut, bytesSent, bytesReceived, ts: now };
    }

    const mos = computeMos({ lossPct, jitterMs, rttMs, codecLabel: codecIn });
    return { mos, bars: mosToBars(mos), lossPct, jitterMs, rttMs, codecIn, codecOut, bytesSent, bytesReceived, ts: now };
  }
}
